import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { clearCart } from '../utils/cartUtils';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [paymentStatus, setPaymentStatus] = useState('checking'); // 'checking', 'success', 'cancelled', 'failed'
  const [loading, setLoading] = useState(true);
  
  const paymentId = searchParams.get('payment_id') || searchParams.get('payment_request_id');
  const queryType = searchParams.get('type');
  
  // Determine payment type from URL path if not in query params
  const getPaymentType = () => {
    if (queryType) return queryType;
    const path = window.location.pathname;
    if (path.includes('program-invoice-success')) return 'invoice';
    if (path.includes('membership-success')) return 'membership';
    if (path.includes('competition-payment-success')) return 'competition';
    if (path.includes('program-payment-success')) return 'program';
    if (path.includes('merchandise-payment-success')) return 'merchandise';
    if (path.includes('qr-payment-success')) return 'program';
    if (path.includes('qr-membership-success')) return 'membership';
    return 'payment';
  };
  
  const type = getPaymentType();
  const status = searchParams.get('status'); // HitPay might include status parameter
  
  const checkPaymentStatus = useCallback(async () => {
    try {
      // If HitPay provides status in URL, use it
      if (status) {
        if (status === 'completed' || status === 'success') {
          setPaymentStatus('success');
          if (type === 'merchandise') {
            clearCart();
          }
        } else if (status === 'cancelled' || status === 'failed') {
          setPaymentStatus('cancelled');
        } else {
          setPaymentStatus('cancelled'); // Default to cancelled for unknown status
        }
        setLoading(false);
        return;
      }

      // CRITICAL: Always verify payment status - don't assume success
      // This prevents showing success page for cancelled payments
      if (paymentId) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        
        // Try different API endpoints based on payment type
        let verifyUrl;
        if (type === 'merchandise') {
          verifyUrl = `${apiUrl}/api/products/verify-payment/${paymentId}`;
        } else if (type === 'membership') {
          verifyUrl = `${apiUrl}/api/membership/verify-payment/${paymentId}`;
        } else if (type === 'competition') {
          verifyUrl = `${apiUrl}/api/competitions/verify-payment/${paymentId}`;
        } else {
          // Default to products endpoint
          verifyUrl = `${apiUrl}/api/products/verify-payment/${paymentId}`;
        }
        
        const response = await fetch(verifyUrl);
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'completed' || data.status === 'success') {
            setPaymentStatus('success');
            if (type === 'merchandise') {
              clearCart();
            }
          } else {
            // Any other status (pending, cancelled, failed, unknown) = cancelled
            setPaymentStatus('cancelled');
          }
        } else {
          // API call failed, assume cancelled for safety
          setPaymentStatus('cancelled');
        }
      } else {
        // No payment ID, definitely cancelled
        setPaymentStatus('cancelled');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('cancelled'); // Default to cancelled for safety
      setLoading(false);
    }
  }, [status, type, paymentId]);

  useEffect(() => {
    // Replace the current history entry to prevent back button issues
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    // Check payment status
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  // Start countdown timer for successful payments
  useEffect(() => {
    if (paymentStatus === 'success' && !loading && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [paymentStatus, loading, countdown]);

  // Navigate when countdown reaches 0
  useEffect(() => {
    if (countdown === 0) {
      const timeout = setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [countdown, navigate]);

  const getTitle = () => {
    switch (type) {
      case 'membership':
        return 'Membership Payment Successful!';
      case 'program':
        return 'Program Registration Successful!';
      case 'merchandise':
        return 'Merchandise Purchase Successful!';
      case 'competition':
        return 'Competition Registration Successful!';
      case 'invoice':
        return 'Invoice Payment Successful!';
      default:
        return 'Payment Successful!';
    }
  };

  const getMessage = () => {
    switch (type) {
      case 'membership':
        return 'Welcome to Oldham Athletics! Your membership is now active.';
      case 'program':
        return 'You have been successfully registered for the program.';
      case 'merchandise':
        return 'Your order has been confirmed and will be processed shortly.';
      case 'competition':
        return 'You have been successfully registered for the competition!';
      case 'invoice':
        return 'Your program invoice has been paid successfully.';
      default:
        return 'Your payment has been processed successfully.';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-blue-100">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              Verifying Payment...
            </h2>
            <p className="mt-2 text-center text-sm text-gray-400">
              Please wait while we confirm your payment status.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show cancelled/failed payment state
  if (paymentStatus === 'cancelled' || paymentStatus === 'failed') {
    const getCancelledMessage = () => {
      switch (type) {
        case 'membership':
          return 'Your membership payment was not completed. You can try again or contact support if you need help.';
        case 'competition':
          return 'Your competition registration payment was not completed. You can try again or contact support if you need help.';
        case 'merchandise':
          return 'Your merchandise payment was not completed. Your items are still in your cart and you can try again.';
        case 'invoice':
          return 'Your invoice payment was not completed. You can try again from your dashboard or contact support if you need help.';
        default:
          return 'Your payment was not completed. You can try again or return to browse our products.';
      }
    };

    const getRetryDestination = () => {
      switch (type) {
        case 'membership':
          return '/register';
        case 'competition':
          return '/competitions';
        case 'merchandise':
          return '/merchandise';
        case 'invoice':
          return '/dashboard';
        default:
          return '/merchandise';
      }
    };

    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-yellow-100">
              <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              Payment Not Completed
            </h2>
            <p className="mt-2 text-center text-sm text-gray-400">
              {getCancelledMessage()}
            </p>
            
            {paymentId && (
              <p className="mt-4 text-center text-xs text-gray-500">
                Payment ID: {paymentId}
              </p>
            )}
          </div>

          <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4">
            <p className="text-yellow-400 text-sm">
              <strong>What happened?</strong><br />
              You were redirected back from the payment page without completing the transaction. 
              This could happen if you clicked "Go back to merchant" or if there was a technical issue.
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => navigate(getRetryDestination(), { replace: true })}
              className="flex-1 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-oldham-gold hover:bg-oldham-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-oldham-gold"
            >
              Try Again
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </button>
            
            <button
              onClick={() => navigate('/', { replace: true })}
              className="flex-1 group relative w-full flex justify-center py-2 px-4 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-transparent hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show success state (only if payment was actually completed)
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100">
            <CheckCircleIcon className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {getTitle()}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            {getMessage()}
          </p>
          
          {paymentId && (
            <p className="mt-4 text-center text-xs text-gray-500">
              Payment ID: {paymentId}
            </p>
          )}
        </div>

        <div className="mt-8 space-y-4">
          <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
            <p className="text-blue-400 text-sm">
              <strong>What's Next?</strong><br />
              {type === 'membership' && 'You can now access all member benefits and register for programs.'}
              {type === 'program' && 'Check your dashboard for program details and upcoming sessions.'}
              {type === 'merchandise' && 'You will receive an email confirmation with order details and tracking information.'}
              {type === 'invoice' && 'Your invoice has been paid. You can view your updated invoice status in your dashboard.'}
              {!['membership', 'program', 'merchandise', 'invoice'].includes(type) && 'Check your dashboard for updated information.'}
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="flex-1 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-oldham-gold hover:bg-oldham-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-oldham-gold"
            >
              Go to Dashboard
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </button>
            
            <button
              onClick={() => navigate('/', { replace: true })}
              className="flex-1 group relative w-full flex justify-center py-2 px-4 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-transparent hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Back to Home
            </button>
          </div>

          <p className="text-center text-xs text-gray-500">
            Automatically redirecting to dashboard in {countdown} seconds...
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
